import { useState, useEffect, useContext, useCallback } from 'react';
import { useFocusTrap } from './hooks/use-focus-trap';
import classNames from 'classnames';
import {
  Empty,
  NOOP,
  useClickOutside,
  useInternalEvents,
  useTranslation,
} from '@testsigma/atoms';
import { useNavigate } from 'react-router-dom';
import { UserPreferenceContext } from '@contexts/user-preference-context';
import { useCreateNewTestCase } from '@modules/test-development/components/cases/list/create-test-case-button';
import { CommandCenterLaunchTour } from './components/tour';
import noDataPane from '@images/no-data-pane.svg';
import { ActionItem } from './components/action-item';
import { HelpPreferenceContext } from '@contexts/help-preference-context';
import { OPEN_USAGE_DETAILS } from '@constants/events';
import AmplitudeEventService from '@services/amplitude-event.service';

enum ActionType {
  ROUTE = 'ROUTE',
  CLICK = 'CLICK',
  HANDLER = 'HANDLER',
}

interface Action {
  title: string;
  description?: string;
  icon?: string;
  iconClass?: string;
  type: ActionType;
  meta?: any;
  showOnlyOnPage?: string;
  showOnlyOnElement?: string;
}

function useCommandK(callback: () => void) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        callback();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [callback]);
}

function useEscapeToClose(callback: () => void) {
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        callback();
      }
    };

    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [callback]);
}

const actions: Action[] = [
  {
    title: 'command.center.updateTestcaseSettings',
    icon: 'arrow-back',
    iconClass: 'rotate-180',
    type: ActionType.CLICK,
    meta: {
      element: '#tabs [data-icon-name="settings"]',
    },
    showOnlyOnPage: 'td/cases',
  },
  {
    title: 'command.center.manageTestcase',
    icon: 'supervised-user-circle',
    type: ActionType.CLICK,
    meta: {
      element: '#tabs [data-icon-name="supervised-user-circle"]',
    },
    showOnlyOnPage: 'td/cases',
  },
  {
    title: 'command.center.dashboard',
    icon: 'arrow-back',
    iconClass: 'rotate-180',
    type: ActionType.ROUTE,
    meta: { pathname: 'dashboard' },
  },
  {
    title: 'command.center.testCasesListPage',
    icon: 'arrow-back',
    iconClass: 'rotate-180',
    type: ActionType.ROUTE,
    meta: { pathname: 'td/cases' },
  },
  {
    title: 'command.center.elementsListPage',
    icon: 'arrow-back',
    iconClass: 'rotate-180',
    type: ActionType.ROUTE,
    meta: { pathname: 'td/elements' },
  },
  {
    title: 'command.center.stepGroupsListPage',
    icon: 'arrow-back',
    iconClass: 'rotate-180',
    type: ActionType.ROUTE,
    meta: { pathname: 'td/step-groups' },
  },
  {
    title: 'command.center.tdp',
    icon: 'arrow-back',
    iconClass: 'rotate-180',
    type: ActionType.ROUTE,
    meta: { pathname: 'td/data' },
  },
  {
    title: 'command.center.envs',
    icon: 'arrow-back',
    iconClass: 'rotate-180',
    type: ActionType.ROUTE,
    meta: { pathname: 'td/environments' },
  },
  {
    title: 'command.center.envs.vars',
    icon: 'arrow-back',
    iconClass: 'rotate-180',
    type: ActionType.ROUTE,
    meta: { pathname: 'td/environments/variables' },
  },
  {
    title: 'command.center.uploads',
    icon: 'arrow-back',
    iconClass: 'rotate-180',
    type: ActionType.ROUTE,
    meta: { pathname: 'td/uploads' },
  },
  {
    title: 'command.center.suites',
    icon: 'arrow-back',
    iconClass: 'rotate-180',
    type: ActionType.ROUTE,
    meta: { pathname: 'td/suites' },
  },
  {
    title: 'command.center.plans',
    icon: 'arrow-back',
    iconClass: 'rotate-180',
    type: ActionType.ROUTE,
    meta: { pathname: 'td/plans' },
  },
  {
    title: 'left.nav.run_result',
    icon: 'arrow-back',
    iconClass: 'rotate-180',
    type: ActionType.ROUTE,
    meta: { pathname: 'td/results' },
  },
  {
    title: 'left.nav.agent',
    icon: 'arrow-back',
    iconClass: 'rotate-180',
    type: ActionType.ROUTE,
    meta: { pathname: 'agents' },
  },
  {
    title: 'left.nav.addons',
    icon: 'arrow-back',
    iconClass: 'rotate-180',
    type: ActionType.ROUTE,
    meta: { pathname: 'addons' },
  },

  {
    title: 'command.center.adminSettings',
    icon: 'arrow-back',
    iconClass: 'rotate-180',
    type: ActionType.ROUTE,
    meta: {
      pathname: 'settings',
    },
  },
  {
    title: 'command.center.settings.phone',
    icon: 'arrow-back',
    iconClass: 'rotate-180',
    type: ActionType.ROUTE,
    meta: {
      pathname: 'settings/phone_numbers',
    },
  },
  {
    title: 'command.center.settings.mailbox',
    icon: 'arrow-back',
    iconClass: 'rotate-180',
    type: ActionType.ROUTE,
    meta: {
      pathname: 'settings/mail_boxes',
    },
  },
  {
    title: 'command.center.settings.ios',
    icon: 'arrow-back',
    iconClass: 'rotate-180',
    type: ActionType.ROUTE,
    meta: {
      pathname: 'settings/provisioning_profiles',
    },
  },

  {
    title: 'command.center.openUsageDetail',
    icon: 'donut-large',
    type: ActionType.HANDLER,
    meta: {
      handler: ({ publishInternalEvents }) =>
        publishInternalEvents(OPEN_USAGE_DETAILS),
    },
  },
  {
    title: 'command.center.contactTestsigmaSupport',
    icon: 'support-agent',
    type: ActionType.HANDLER,
    meta: {
      handler: () => window?.fcWidget?.open(),
    },
  },
  {
    title: 'command.center.listLastFailedRunResults',
    icon: 'cancel-circle-outline',
    type: ActionType.ROUTE,
    meta: {
      pathname: 'td/cases/filters/2?q=result@FAILURE',
    },
  },
  {
    title: 'command.center.addNewUser',
    icon: 'add',
    iconClass: '!text-2xl',
    type: ActionType.ROUTE,
    meta: {
      pathname: 'settings/users#add',
    },
  },
  // {
  //   title: 'command.center.createNewTestCaseAndLaunchRecorder',
  //   icon: 'test-development',
  //   type: ActionType.HANDLER,
  //   meta: {
  //     handler: ({ createNewTestCase }) => createNewTestCase('#record'),
  //   },
  // },
  {
    title: 'command.center.editProjectSettings',
    icon: 'settings',
    type: ActionType.HANDLER,
    meta: {
      handler: ({ publishInternalEvents }) =>
        publishInternalEvents('open-project-settings'),
    },
  },
];

export const CommandCenter = () => {
  const { t } = useTranslation();
  const [isOpened, updateOpened] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [inputMethod, setInputMethod] = useState<'mouse' | 'keyboard'>(
    'keyboard',
  );
  const { helpPreference } = useContext(HelpPreferenceContext);

  const navigate = useNavigate();
  const { userPreference, updatePreferenceContext, setOrUpdatePreference } =
    useContext(UserPreferenceContext);
  const { createNewTestCase } = useCreateNewTestCase(true, false);
  const publishInternalEvents = useInternalEvents();
  const containerRef = useFocusTrap(isOpened);

  const filteredActions = actions.filter(
    (action) =>
      (t(action.title).toLowerCase().includes(query.toLowerCase()) &&
        !action.showOnlyOnPage) ||
      window.location.pathname.includes(action.showOnlyOnPage),
  );

  const closeHandler = () => {
    updateOpened(false);
    setQuery('');
    setHighlightedIndex(0);
  };

  const openHandler = useCallback(() => {
    if (document.querySelector('[role="overlay"]')) return;

    if (!userPreference.preference.showCommandCenterTooltip) {
      userPreference.preference.showCommandCenterTooltip = true;
      updatePreferenceContext(userPreference);
      setOrUpdatePreference(userPreference, NOOP);
    }

    updateOpened(true);
    AmplitudeEventService.commandCenterLaunch();
  }, [updatePreferenceContext, userPreference, setOrUpdatePreference]);

  useCommandK(openHandler);
  useEscapeToClose(closeHandler);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
    setHighlightedIndex(0);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    setInputMethod('keyboard');

    if (event.key === 'ArrowDown') {
      setHighlightedIndex((prevIndex) =>
        prevIndex < filteredActions.length - 1 ? prevIndex + 1 : prevIndex,
      );
    } else if (event.key === 'ArrowUp') {
      setHighlightedIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : prevIndex,
      );
    } else if (event.key === 'Enter') {
      const action = filteredActions[highlightedIndex];
      if (action) {
        handleAction(action);
      }
    }
  };

  const handleAction = (action: Action) => {
    try {
      AmplitudeEventService.commandCenterExecute(action.title);

      if (action.type === ActionType.ROUTE) {
        const route = action.meta.pathname.replace(
          'td/',
          `/td/${userPreference?.preference?.projectPreference?.versionId}/`,
        );
        navigate(route);
      } else if (action.type === ActionType.HANDLER) {
        action.meta.handler({
          createNewTestCase,
          navigate,
          publishInternalEvents,
        });
      } else if (action.type === ActionType.CLICK) {
        document.querySelector(action.meta.element)?.click();
      }
    } catch (e) {}

    closeHandler();
  };

  useClickOutside(containerRef, closeHandler);

  if (
    !isOpened &&
    !userPreference?.preference?.showCommandCenterTooltip &&
    !helpPreference?.hasOnBoarding &&
    !document.querySelector('[role="overlay"]')
  )
    return <CommandCenterLaunchTour />;

  return (
    isOpened && (
      <div className='fixed top-0 left-0 w-screen h-screen flex justify-center z-[999999]'>
        <div
          ref={containerRef}
          className={classNames([
            'w-[640px] h-[400px] border-[0.5px] border-secondary-200 rounded-lg',
            'bg-white mt-[120px] flex flex-col overflow-hidden',
          ])}
          style={{
            boxShadow:
              'rgba(0, 0, 0, 0.12) 0px 4px 30px, rgba(0, 0, 0, 0.04) 0px 3px 17px, rgba(0, 0, 0, 0.04) 0px 2px 8px, rgba(0, 0, 0, 0.04) 0px 1px 1px',
          }}
        >
          {/* Header */}
          <div className='flex border-b border-b-secondary-200'>
            <input
              type='text'
              placeholder={t('command.center.typeCommand')}
              value={query}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              className='outline-none w-[200px] h-[54px] px-4 flex-grow font-light'
            />
          </div>
          {/* Results */}
          <div className='flex-grow overflow-auto p-2 gap-1 flex flex-col'>
            {filteredActions.map((action, index) => (
              <ActionItem
                action={action}
                key={`${index}`}
                handleAction={handleAction}
                isActiveItem={highlightedIndex == index}
                index={index}
                setHighlightedIndex={setHighlightedIndex}
                inputMethod={inputMethod}
                setInputMethod={setInputMethod}
              />
            ))}
            {filteredActions.length < 1 && (
              <Empty
                description={t('command.center.searchNotFound')}
                className='!text-sm'
                imageSource={noDataPane}
              />
            )}
          </div>
        </div>
      </div>
    )
  );
};
